"use client";

import type { Column, Table } from "@tanstack/react-table";
import { X, Plus } from "lucide-react";
import * as React from "react";

import { DataTableDateFilter } from "@/components/data-table/data-table-date-filter";
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter";
import { DataTableSliderFilter } from "@/components/data-table/data-table-slider-filter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { FilterDescriptor } from "@/lib/erp/filterTypes";

type TranslationModule = Record<string, string>;

interface DataTableToolbarProps<TData> extends React.ComponentProps<"div"> {
  table: Table<TData>;
  onAddFilter?: () => void;
  filterCount?: number;
  translations?: TranslationModule;
  filters?: FilterDescriptor[];
  onFiltersChange?: (filters: FilterDescriptor[]) => void;
}

export function DataTableToolbar<TData>({
  table,
  children,
  className,
  onAddFilter,
  filterCount = 0,
  translations,
  filters,
  onFiltersChange,
  ...props
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const columns = React.useMemo(
    () => table.getAllColumns().filter((column) => column.getCanFilter()),
    [table],
  );

  // Find the index of the item_group column
  const itemGroupIndex = React.useMemo(
    () => columns.findIndex((column) => column.id === "item_group"),
    [columns],
  );

  // Handler for item_group server filter updates
  const handleItemGroupServerFilterChange = React.useCallback(
    (values: string[]) => {
      if (!onFiltersChange) return;

      const currentFilters = filters ?? [];

      // Remove any existing item_group filter
      const withoutItemGroup = currentFilters.filter(
        (f) => f.field !== "item_group"
      );

      if (!values.length) {
        // No selection → no item_group filter
        onFiltersChange(withoutItemGroup);
        return;
      }

      const newFilter: FilterDescriptor = {
        field: "item_group",
        operator: values.length === 1 ? "equals" : "in",
        value: values.length === 1 ? values[0] : values,
      };

      onFiltersChange([...withoutItemGroup, newFilter]);
    },
    [onFiltersChange, filters],
  );

  const onReset = React.useCallback(() => {
    table.resetColumnFilters();
  }, [table]);

  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn(
        "flex w-full",
        !onAddFilter && "flex-row items-center gap-3 p-0", // Row layout when Add Filter is handled externally
        onAddFilter && "flex-col gap-3 p-4",
        className,
      )}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 flex-1">
        {columns.map((column, index) => {
          // Insert Add Filter button right before item_group
          if (onAddFilter && index === itemGroupIndex && itemGroupIndex !== -1) {
            return (
              <React.Fragment key={`add-filter-${column.id}`}>
                <Button
                  onClick={onAddFilter}
                  variant="ghost"
                  size="default"
                  className="h-14 px-4 text-sm font-normal rounded-full bg-white dark:bg-gray-900 border border-border hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40"
                  aria-label={filterCount > 0 ? (translations?.manageFilters || "Manage filters") : (translations?.addFilter || "Add filter")}
                >
                  <Plus className="size-4 mr-2" />
                  {filterCount > 0 ? (translations?.filters || "Filters") : (translations?.addFilter || "Add Filter")}
                  {filterCount > 0 && (
                    <Badge 
                      variant="secondary" 
                      className="ml-2 h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold"
                    >
                      {filterCount}
                    </Badge>
                  )}
                </Button>
                <DataTableToolbarFilter 
                  key={`${column.id}-${column.columnDef.meta?.label || ''}`} 
                  column={column} 
                  handleItemGroupServerFilterChange={handleItemGroupServerFilterChange}
                />
              </React.Fragment>
            );
          }
          return (
            <DataTableToolbarFilter 
              key={`${column.id}-${column.columnDef.meta?.label || ''}`} 
              column={column} 
              handleItemGroupServerFilterChange={handleItemGroupServerFilterChange}
            />
          );
        })}
        {/* If Add Filter button is provided but item_group doesn't exist, show it at the end */}
        {onAddFilter && itemGroupIndex === -1 && (
          <Button
            onClick={onAddFilter}
            variant="ghost"
            size="default"
            className="h-14 px-4 text-sm font-normal rounded-full bg-white dark:bg-gray-900 border border-border hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40"
            aria-label={filterCount > 0 ? (translations?.manageFilters || "Manage filters") : (translations?.addFilter || "Add filter")}
          >
            <Plus className="size-4 mr-2" />
            {filterCount > 0 ? (translations?.filters || "Filters") : (translations?.addFilter || "Add Filter")}
            {filterCount > 0 && (
              <Badge 
                variant="secondary" 
                className="ml-2 h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold"
              >
                {filterCount}
              </Badge>
            )}
          </Button>
        )}
        {isFiltered && (
          <Button
            aria-label={translations?.resetFilters || "Reset filters"}
            variant="outline"
            size="default"
            className="border-dashed h-14 px-5 text-base rounded-md"
            onClick={onReset}
          >
            <X className="size-5" />
            {translations?.reset || "Reset"}
          </Button>
        )}
      </div>
      {children && (
        <div className="ml-auto shrink-0">
          {children}
        </div>
      )}
    </div>
  );
}
interface DataTableToolbarFilterProps<TData> {
  column: Column<TData, unknown>;
  handleItemGroupServerFilterChange?: (values: string[]) => void;
}

function DataTableToolbarFilter<TData>({
  column,
  handleItemGroupServerFilterChange,
}: DataTableToolbarFilterProps<TData>) {
  // Read columnMeta directly from column to ensure we get the latest value
  // Don't memoize this - we want it to update when columns change
  const columnMeta = column.columnDef.meta;

  if (!columnMeta?.variant) return null;

  switch (columnMeta.variant) {
    case "text":
      return (
        <Input
          placeholder={columnMeta.placeholder ?? columnMeta.label}
          value={(column.getFilterValue() as string) ?? ""}
          onChange={(event) => column.setFilterValue(event.target.value)}
          className="h-14 w-full min-w-[200px] sm:min-w-[240px] md:min-w-[280px] text-base rounded-full bg-white dark:bg-gray-900 border border-border"
        />
      );

    case "number":
      return (
        <div className="relative">
          <Input
            type="number"
            inputMode="numeric"
            placeholder={columnMeta.placeholder ?? columnMeta.label}
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(event) => column.setFilterValue(event.target.value)}
            className={cn("h-14 w-full min-w-[200px] sm:min-w-[240px] md:min-w-[280px] text-base rounded-full bg-white dark:bg-gray-900 border border-border", columnMeta.unit && "pr-12")}
          />
          {columnMeta.unit && (
            <span className="absolute top-1/2 right-3 -translate-y-1/2 flex items-center rounded-sm bg-accent/60 backdrop-blur-sm px-3 py-1.5 text-muted-foreground text-sm font-medium">
              {columnMeta.unit}
            </span>
          )}
        </div>
      );

    case "range":
      return (
        <DataTableSliderFilter
          column={column}
          title={columnMeta.label ?? column.id}
        />
      );

    case "date":
    case "dateRange":
      return (
        <DataTableDateFilter
          column={column}
          title={columnMeta.label ?? column.id}
          multiple={columnMeta.variant === "dateRange"}
        />
      );

    case "select":
    case "multiSelect":
      return (
        <DataTableFacetedFilter
          column={column}
          title={columnMeta.label ?? column.id}
          options={columnMeta.options ?? []}
          multiple={columnMeta.variant === "multiSelect"}
          onServerFilterChange={
            column.id === "item_group"
              ? handleItemGroupServerFilterChange
              : undefined
          }
        />
      );

    default:
      return null;
  }
}
